import { type RefObject, useEffect, useRef } from 'react';

import type { ReadableStore } from '@frontend/viewer-core';

import { MEASURE_COLOR, SNAP_RING_RADIUS_PX } from '../constants';
import type {
  MeasureManager,
  MeasureTarget,
  PointerState,
  ToolId,
} from '../session/types';
import {
  type MeasurementFormat,
  formatLength,
} from '../utils/formatMeasurement';
import {
  type ToolViewport,
  bindOverlayFrame,
  create,
  createLabelledLine,
  placeLabelledLine,
  setAttributes,
  show,
} from '../utils/toolUtils';

export type MeasureOverlayDeps = {
  measure: Pick<
    MeasureManager,
    'getState' | 'subscribe' | 'preview' | 'modifiers'
  >;
  tool: ReadableStore<ToolId>;
  pointer: ReadableStore<PointerState>;
  viewport: ToolViewport;
  format: MeasurementFormat;
};

/**
 * Measure tool: a labelled distance, the lines and circles it measures between,
 * and the snap ring under the cursor.
 *
 * Committed targets live on the reactive tier; the hovered target and the
 * preview follow the pointer on the live tier. Drawn in SVG so the length is
 * real text, and rewritten per frame so a move never commits React.
 */
export const useDxfMeasureOverlay = ({
  measure,
  tool,
  pointer,
  viewport,
  format,
}: MeasureOverlayDeps): RefObject<SVGSVGElement> => {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) {
      return;
    }

    const litTargets = create('path');
    setAttributes(litTargets, {
      fill: 'none',
      stroke: MEASURE_COLOR,
      'stroke-width': 2.5,
      'stroke-linecap': 'round',
    });
    svg.appendChild(litTargets);

    const measureLine = createLabelledLine(MEASURE_COLOR, true);
    svg.appendChild(measureLine.group);

    const snapRing = create('circle');
    setAttributes(snapRing, {
      r: SNAP_RING_RADIUS_PX,
      fill: 'none',
      stroke: MEASURE_COLOR,
      'stroke-width': 1.5,
    });
    svg.appendChild(snapRing);

    /** A line or circle as it appears on screen. Anything else is not lit. */
    const outlineOf = (target: MeasureTarget | null | undefined): string => {
      if (target?.kind === 'line') {
        const [a, b] = target.segment.map(viewport.worldToScreen);
        return `M${a[0]} ${a[1]}L${b[0]} ${b[1]}`;
      }
      if (target?.kind === 'circle') {
        // The view only scales, flips and turns by quarters, so a circle stays
        // a circle on screen.
        const [cx, cy] = viewport.worldToScreen(target.center);
        const r = target.radius * viewport.getState().scale;
        return `M${cx - r} ${cy}a${r} ${r} 0 1 0 ${2 * r} 0a${r} ${r} 0 1 0 ${
          -2 * r
        } 0`;
      }
      return '';
    };

    const draw = () => {
      const { from, measurement } = measure.getState();
      const pointerState = pointer.getState();
      const isMeasuring = tool.getState() === 'measure';
      const hovered = isMeasuring ? pointerState.target : null;

      const outline = [from, measurement?.to, hovered].map(outlineOf).join('');
      show(litTargets, outline !== '');
      litTargets.setAttribute('d', outline);

      const fixedOrPreview = measurement ?? measure.preview(hovered);
      show(measureLine.group, Boolean(fixedOrPreview));
      if (fixedOrPreview) {
        placeLabelledLine(
          measureLine,
          viewport.worldToScreen(fixedOrPreview.start),
          viewport.worldToScreen(fixedOrPreview.end),
          formatLength(fixedOrPreview.distance, format),
        );
        // A fixed measurement stops chasing the cursor.
        measureLine.line.setAttribute(
          'stroke-dasharray',
          measurement ? 'none' : '6 4',
        );
      }

      const snapScreen =
        isMeasuring && pointerState.snap
          ? viewport.worldToScreen(pointerState.snap.point)
          : null;
      show(snapRing, Boolean(snapScreen));
      if (snapScreen) {
        setAttributes(snapRing, { cx: snapScreen[0], cy: snapScreen[1] });
      }
    };

    return bindOverlayFrame({
      draw,
      subscriptions: [
        viewport.subscribe,
        viewport.size.subscribe,
        pointer.subscribe,
        measure.subscribe,
        // The preview reads the modifiers, so a key changes it on its own.
        measure.modifiers.subscribe,
        tool.subscribe,
      ],
      onCleanup: () => svg.replaceChildren(),
    });
  }, [measure, tool, pointer, viewport, format]);

  return svgRef;
};
